/* eslint-disable no-magic-numbers */

import { difference } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getLocalJSFilenames, getServerJSFilenames } from './utils';

const LAST_RELOAD_LOCAL_STORAGE_KEY = 'lastAutoReloadSignature';
const LOOP_TIME_MS = 1800000; // 30 mins

/**
 * Will periodically make a request to retrieve the root index.html file for the app, extract the JS
 * filenames contained within it, and see if we dont have any of those files already loaded.
 * @param reloadImmediately - when true and a new version is detected, the page will be reloaded immediately.
 */
export const useLatestVersionDetection = (reloadImmediately = false) => {
  const currentJSFiles = useMemo(getLocalJSFilenames, []);
  const latestSignature = useRef('');
  const loop = useRef<number>();
  const [newVersionDetected, setNewVersionDetected] = useState(false);

  const checkForNewVersion = useCallback(async () => {
    try {
      const lastReloadSignature = localStorage.getItem(LAST_RELOAD_LOCAL_STORAGE_KEY);
      const latestJSFiles = await getServerJSFilenames();
      latestSignature.current = [...latestJSFiles].sort().join('');

      if (difference(latestJSFiles, currentJSFiles).length !== 0) {
        window.clearInterval(loop.current);

        if (lastReloadSignature !== latestSignature.current) {
          setNewVersionDetected(true);
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            'A new version of Predict was detected, but the last reload failed to load it (likely due to a caching issue).'
          );
        }
      } else {
        // If we got here, we're running the latest version. Clear out this key.
        localStorage.removeItem(LAST_RELOAD_LOCAL_STORAGE_KEY);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to check for latest version of Predict', e);
    }
  }, [currentJSFiles]);

  const updateLastReloadSignature = useCallback(() => {
    localStorage.setItem(LAST_RELOAD_LOCAL_STORAGE_KEY, latestSignature.current);
  }, []);

  useEffect(() => {
    if (!newVersionDetected) {
      // If first run, check immediately
      if (typeof loop.current === 'undefined') {
        checkForNewVersion();
      }

      // Now set up loop to keep checking periodically
      window.clearInterval(loop.current);
      loop.current = window.setInterval(() => {
        checkForNewVersion();
      }, LOOP_TIME_MS);

      return () => {
        window.clearInterval(loop.current);
      };
    }
  }, [checkForNewVersion, newVersionDetected]);

  useEffect(() => {
    if (newVersionDetected && reloadImmediately) {
      updateLastReloadSignature();
      window.location.reload();
    }
  }, [newVersionDetected, reloadImmediately, updateLastReloadSignature]);

  return { newVersionDetected, updateLastReloadSignature };
};
